/* ─── GraphPopovers ──────────────────────────────────────────────────
   Les micro-éditeurs de la vue graphe.

   Principe : ouvrir l'éditeur complet (PageComposer / DecisionEditor dans un
   drawer) pour changer une condition d'une ligne est disproportionné. Ces
   popovers couvrent les gestes rapides faits DEPUIS le graphe ; le drawer
   reste disponible pour l'édition de fond.
*/

import React, { useState, useEffect, useRef, useMemo } from "react";
import { isValidSlug, SLUG_HINT } from "./BuilderUtils";
import { PAGE_TYPE_INFO, displayInfoOf } from "./pageKinds";

/* Positionne un popover près d'un point écran sans déborder du viewport. */
function anchorStyle(anchor, width = 320, height = 300) {
  const pad = 12;
  const x = Math.min(Math.max(pad, anchor.x), window.innerWidth - width - pad);
  const y = Math.min(Math.max(pad, anchor.y), window.innerHeight - height - pad);
  return { left: x, top: y, width };
}

function useDismiss(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);
  return ref;
}

/* ─── Éditeur de condition d'une conséquence ────────────────────────
   Le moteur ne supporte PAS les mêmes conditions des deux côtés :
   - conséquence de décision (game_functions.py) → uniquement
     `previous_decision_id` ("si telle décision a été prise") ;
   - conséquence générale de page → condition_metadata complet
     (past_decision / score_below / score_above).
   On n'expose donc que ce qui aura réellement un effet, sinon l'auteur
   configure une condition qui ne se déclenchera jamais.
*/
export function ConditionPopover({ anchor, edge, pages, decisions, scoreDic, configId, onApply, onDelete, onClose }) {
  const ref = useDismiss(onClose);
  const isDecisionConsequence = edge.ref?.doc === "decision";

  const consequence = edge.consequence || {};
  const meta = consequence.condition_metadata || {};

  const initialMode = !consequence.condition
    ? "always"
    : isDecisionConsequence || meta.type === "past_decision"
      ? "past_decision"
      : meta.type === "score_below"
        ? "score_below"
        : meta.type === "score_above"
          ? "score_above"
          : "past_decision";

  const [mode, setMode] = useState(initialMode);
  const [decisionId, setDecisionId] = useState(
    consequence.previous_decision_id || meta.previous_decision_id || ""
  );
  const [scoreId, setScoreId] = useState(meta.score_id || "");
  const [value, setValue] = useState(meta.value ?? 0);
  const [targetId, setTargetId] = useState(edge.targetPageId || "");
  const [search, setSearch] = useState("");

  const filteredPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? pages.filter((p) => (p.title || "").toLowerCase().includes(q) || p._id.toLowerCase().includes(q))
      : pages;
    return list.slice(0, 60);
  }, [pages, search]);

  const apply = () => {
    const patch = { consequence_id: targetId };
    if (mode === "always") {
      patch.condition = false;
    } else if (mode === "past_decision") {
      patch.condition = true;
      if (isDecisionConsequence) patch.previous_decision_id = decisionId;
      else patch.condition_metadata = { type: "past_decision", previous_decision_id: decisionId };
    } else {
      patch.condition = true;
      patch.condition_metadata = { type: mode, score_id: scoreId, value: Number(value) };
    }
    onApply(patch);
  };

  const variationDriven = edge.variationDriven;

  return (
    <div className="graph-popover" style={anchorStyle(anchor, 360, 420)} ref={ref}>
      <div className="graph-popover-header">
        <strong>Conséquence</strong>
        <button className="builder-icon-btn" onClick={onClose} title="Fermer">✕</button>
      </div>

      {variationDriven && (
        <div className="builder-warning-inline">
          Cette conséquence est pilotée par une variante propre à cet atelier
          (<code>variation_game_config</code>). La modification n'affectera que « {configId} ».
        </div>
      )}

      <label className="graph-popover-label">Condition</label>
      <select className="builder-input" value={mode} onChange={(e) => setMode(e.target.value)}>
        <option value="always">Toujours</option>
        <option value="past_decision">Si une décision a été prise</option>
        {!isDecisionConsequence && <option value="score_below">Si un score est en dessous de…</option>}
        {!isDecisionConsequence && <option value="score_above">Si un score est au dessus de…</option>}
      </select>
      {isDecisionConsequence && (
        <p className="graph-popover-hint">
          Le moteur n'évalue que les conditions sur une décision passée pour les
          conséquences de mesure. Pour un seuil de score, utilisez une conséquence
          générale de page.
        </p>
      )}

      {mode === "past_decision" && (
        <>
          <label className="graph-popover-label">Décision requise</label>
          <select className="builder-input" value={decisionId} onChange={(e) => setDecisionId(e.target.value)}>
            <option value="">— choisir —</option>
            {decisions.map((d) => (
              <option key={d._id} value={d._id}>{d.title || d._id}</option>
            ))}
          </select>
        </>
      )}

      {(mode === "score_below" || mode === "score_above") && (
        <div className="graph-popover-row">
          <div style={{ flex: 1 }}>
            <label className="graph-popover-label">Score</label>
            <select className="builder-input" value={scoreId} onChange={(e) => setScoreId(e.target.value)}>
              <option value="">— choisir —</option>
              {Object.entries(scoreDic || {}).map(([id, s]) => (
                <option key={id} value={id}>{s.name || id}</option>
              ))}
            </select>
          </div>
          <div style={{ width: 90 }}>
            <label className="graph-popover-label">Valeur</label>
            <input className="builder-input" type="number" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        </div>
      )}

      <label className="graph-popover-label">Page ajoutée à la pile</label>
      <input
        className="builder-input"
        placeholder="Rechercher une page…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="graph-popover-list">
        {filteredPages.map((p) => (
          <button
            key={p._id}
            type="button"
            className={`graph-popover-option ${targetId === p._id ? "selected" : ""}`}
            onClick={() => setTargetId(p._id)}
          >
            <span>{displayInfoOf(p)?.icon || "📄"}</span>
            <span className="graph-popover-option-title">{p.title || p._id}</span>
          </button>
        ))}
        {filteredPages.length === 0 && <p className="graph-popover-hint">Aucune page trouvée.</p>}
      </div>

      <div className="graph-popover-actions">
        <button className="builder-btn danger" onClick={onDelete}>Supprimer</button>
        <button className="builder-btn primary" onClick={apply} disabled={!targetId}>Appliquer</button>
      </div>
    </div>
  );
}

/* ─── Sélecteur de page (fin de drag depuis un port) ────────────── */
export function PagePickerPopover({ anchor, title, pages, existingIds, onPick, onCreate, onClose }) {
  const ref = useDismiss(onClose);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newId, setNewId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("context");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? pages.filter((p) => (p.title || "").toLowerCase().includes(q) || p._id.toLowerCase().includes(q))
      : pages;
    return list.slice(0, 60);
  }, [pages, search]);

  const idTaken = existingIds.has(newId);
  const canCreate = isValidSlug(newId) && !idTaken && newTitle.trim();

  return (
    <div className="graph-popover" style={anchorStyle(anchor, 340, 420)} ref={ref}>
      <div className="graph-popover-header">
        <strong>{title}</strong>
        <button className="builder-icon-btn" onClick={onClose} title="Fermer">✕</button>
      </div>

      {!creating && (
        <>
          <input
            ref={inputRef}
            className="builder-input"
            placeholder="Rechercher une page…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="graph-popover-list">
            {filtered.map((p) => (
              <button key={p._id} type="button" className="graph-popover-option" onClick={() => onPick(p._id)}>
                <span>{displayInfoOf(p)?.icon || "📄"}</span>
                <span className="graph-popover-option-title">{p.title || p._id}</span>
                <span className="graph-popover-option-type">{p.type}</span>
              </button>
            ))}
            {filtered.length === 0 && <p className="graph-popover-hint">Aucune page trouvée.</p>}
          </div>
          <div className="graph-popover-actions">
            <button className="builder-btn" onClick={() => setCreating(true)}>+ Créer une page</button>
          </div>
        </>
      )}

      {creating && (
        <>
          <label className="graph-popover-label">Identifiant</label>
          <input className="builder-input" value={newId} onChange={(e) => setNewId(e.target.value)} placeholder="page_consequence_x" />
          {newId && !isValidSlug(newId) && <p className="builder-warning-inline">{SLUG_HINT}</p>}
          {idTaken && <p className="builder-warning-inline">Cet identifiant existe déjà.</p>}

          <label className="graph-popover-label">Titre</label>
          <input className="builder-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />

          <label className="graph-popover-label">Type</label>
          <select className="builder-input" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {Object.keys(PAGE_TYPE_INFO).map((t) => (
              <option key={t} value={t}>{PAGE_TYPE_INFO[t].icon} {t}</option>
            ))}
          </select>

          <div className="graph-popover-actions">
            <button className="builder-btn" onClick={() => setCreating(false)}>Retour</button>
            <button
              className="builder-btn primary"
              disabled={!canCreate}
              onClick={() => onCreate({ _id: newId, title: newTitle, type: newType })}
            >
              Créer et relier
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Palette de commandes (Cmd+K) ──────────────────────────────── */
export function CommandPalette({ nodes, decisions, onSelectNode, onSelectDecision, onClose }) {
  const ref = useDismiss(onClose);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pageHits = nodes
      .filter((n) => !n.missing && n.page)
      .filter((n) => !q || (n.page.title || "").toLowerCase().includes(q) || n.pageId.toLowerCase().includes(q))
      .map((n) => ({ kind: "page", id: n.id, label: n.page.title || n.pageId, sub: n.pageId, icon: displayInfoOf(n.page)?.icon || "📄" }));

    const decisionHits = decisions
      .filter((d) => !q || (d.title || "").toLowerCase().includes(q) || d._id.toLowerCase().includes(q))
      .map((d) => ({ kind: "decision", id: d._id, label: d.title || d._id, sub: d._id, icon: "◆" }));

    // Dédoublonne les pages réutilisées : on garde la première occurrence.
    const seen = new Set();
    const uniquePages = pageHits.filter((h) => (seen.has(h.sub) ? false : seen.add(h.sub)));
    return [...uniquePages, ...decisionHits].slice(0, 40);
  }, [nodes, decisions, query]);

  useEffect(() => { setCursor(0); }, [query]);

  const choose = (item) => {
    if (!item) return;
    if (item.kind === "page") onSelectNode(item.id);
    else onSelectDecision(item.id);
  };

  return (
    <div className="graph-palette-backdrop">
      <div className="graph-palette" ref={ref}>
        <input
          ref={inputRef}
          className="graph-palette-input"
          placeholder="Rechercher une page ou une mesure…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
            if (e.key === "Enter") { e.preventDefault(); choose(results[cursor]); }
          }}
        />
        <div className="graph-palette-list">
          {results.map((r, i) => (
            <button
              key={`${r.kind}-${r.id}`}
              type="button"
              className={`graph-palette-item ${i === cursor ? "active" : ""}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => choose(r)}
            >
              <span className="graph-palette-icon">{r.icon}</span>
              <span className="graph-palette-label">{r.label}</span>
              <span className="graph-palette-sub">{r.kind === "page" ? "page" : "mesure"} · {r.sub}</span>
            </button>
          ))}
          {results.length === 0 && <p className="graph-popover-hint" style={{ padding: 12 }}>Aucun résultat.</p>}
        </div>
      </div>
    </div>
  );
}

/* ─── Panneau des résultats du validateur ───────────────────────── */
const SEVERITY_ORDER = { error: 0, warning: 1, info: 2 };
const SEVERITY_LABEL = { error: "Erreurs", warning: "Avertissements", info: "Informations" };

export function FindingsPanel({ findings, dirty, onFocusPage, onClose }) {
  const [visibleSeverities, setVisibleSeverities] = useState(new Set(["error", "warning"]));

  const counts = useMemo(() => {
    const c = { error: 0, warning: 0, info: 0 };
    findings.forEach((f) => { c[f.severity] = (c[f.severity] || 0) + 1; });
    return c;
  }, [findings]);

  const shown = useMemo(
    () => findings
      .filter((f) => visibleSeverities.has(f.severity))
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]),
    [findings, visibleSeverities]
  );

  const toggle = (sev) => setVisibleSeverities((prev) => {
    const next = new Set(prev);
    if (next.has(sev)) next.delete(sev); else next.add(sev);
    return next;
  });

  return (
    <div className="graph-findings">
      <div className="graph-findings-header">
        <strong>Vérification</strong>
        <button className="builder-icon-btn" onClick={onClose} title="Fermer">✕</button>
      </div>

      {dirty && (
        <div className="builder-warning-inline">
          Vous avez des modifications non enregistrées : cette vérification porte
          sur l'état actuellement en base, pas sur votre brouillon.
        </div>
      )}

      <div className="graph-findings-filters">
        {["error", "warning", "info"].map((sev) => (
          <button
            key={sev}
            className={`graph-findings-chip sev-${sev} ${visibleSeverities.has(sev) ? "" : "off"}`}
            onClick={() => toggle(sev)}
          >
            {SEVERITY_LABEL[sev]} · {counts[sev] || 0}
          </button>
        ))}
      </div>

      <div className="graph-findings-list">
        {shown.map((f, i) => (
          <button
            key={i}
            type="button"
            className={`graph-finding sev-${f.severity}`}
            onClick={() => f.page_id && onFocusPage(f.page_id)}
            title={f.page_id ? "Centrer sur cette page" : undefined}
          >
            <span className="graph-finding-code">{f.code}</span>
            <span className="graph-finding-msg">{f.message}</span>
            {(f.page_id || f.decision_id) && (
              <span className="graph-finding-loc">
                {f.page_id && `page ${f.page_id}`}
                {f.page_id && f.decision_id && " · "}
                {f.decision_id && `mesure ${f.decision_id}`}
              </span>
            )}
          </button>
        ))}
        {shown.length === 0 && (
          <p className="graph-popover-hint" style={{ padding: 12 }}>
            Aucun problème dans les catégories affichées.
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── Barre de modifications non enregistrées ───────────────────── */
export function ChangesBar({ changes, onSave, onDiscard, onUndo, saving }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="graph-changes-bar">
      <button className="graph-changes-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? "▾" : "▸"} {changes.length} modification{changes.length > 1 ? "s" : ""} non enregistrée{changes.length > 1 ? "s" : ""}
      </button>
      {open && (
        <ul className="graph-changes-list">
          {changes.map((c) => <li key={c.id}>{c.label}</li>)}
        </ul>
      )}
      <div className="graph-changes-actions">
        <button className="builder-btn" onClick={onUndo} disabled={saving} title="Cmd/Ctrl + Z">↶ Annuler</button>
        <button className="builder-btn" onClick={onDiscard} disabled={saving}>Tout abandonner</button>
        <button className="builder-btn primary" onClick={onSave} disabled={saving}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}

/* ─── Dialogue de confirmation de sauvegarde ────────────────────── */
export function SaveDialog({ state, onConfirm, onForce, onClose }) {
  if (!state) return null;
  const { affectedConfigs = [], blocked, entity, id, result, error } = state;

  return (
    <div className="graph-palette-backdrop">
      <div className="graph-save-dialog">
        <h3>{blocked ? "Sauvegarde refusée" : error ? "Échec de la sauvegarde" : "Confirmer l'enregistrement"}</h3>

        {blocked && (
          <>
            <p>
              L'enregistrement de {entity === "config" ? "l'atelier" : entity === "page" ? "la page" : "la mesure"}{" "}
              <code>{id}</code> introduirait des erreurs bloquantes :
            </p>
            <div className="graph-save-errors">
              {Object.entries(result?.errors_by_config || {}).map(([cid, errs]) => (
                <div key={cid}>
                  <strong>{cid}</strong>
                  <ul>{errs.map((e, i) => <li key={i}>{e.message}</li>)}</ul>
                </div>
              ))}
              {result?.findings && (
                <ul>
                  {result.findings.filter((f) => f.severity === "error").map((f, i) => <li key={i}>{f.message}</li>)}
                </ul>
              )}
            </div>
          </>
        )}

        {error && <p className="builder-warning-inline">{error}</p>}

        {!blocked && !error && (
          <>
            <p>Les pages et les mesures sont partagées entre ateliers.</p>
            {affectedConfigs.length > 0 ? (
              <p>
                Cet enregistrement s'appliquera à <strong>{affectedConfigs.length} atelier
                {affectedConfigs.length > 1 ? "s" : ""}</strong> : {affectedConfigs.join(", ")}.
              </p>
            ) : (
              <p>Aucun autre atelier n'est concerné.</p>
            )}
          </>
        )}

        <div className="graph-popover-actions">
          <button className="builder-btn" onClick={onClose}>Annuler</button>
          {blocked && <button className="builder-btn danger" onClick={onForce}>Forcer l'enregistrement</button>}
          {!blocked && !error && <button className="builder-btn primary" onClick={onConfirm}>Enregistrer</button>}
        </div>
      </div>
    </div>
  );
}
